import React from 'react';
import { useTranslation } from 'react-i18next';

interface DraggableItem {
  type: 'activity' | 'appointment';
  name: string;
  icon: string;
  color: string;
  calendarTypeOverride?: 'practitioner' | 'employee';
}

interface CalendarDragSidebarProps {
  onDragStart: (item: DraggableItem) => void;
  onDragEnd: () => void;
  allowAppointments?: boolean;
}

export function CalendarDragSidebar({ onDragStart, onDragEnd, allowAppointments = true }: CalendarDragSidebarProps) {
  const { t } = useTranslation();

  const handleDragStart = (e: React.DragEvent, item: DraggableItem) => {
    console.log('Drag started:', item);
    
    // Set drag data - use both formats for compatibility
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
    e.dataTransfer.setData('application/x-calendar-item', JSON.stringify(item));
    e.dataTransfer.setData('text/html', JSON.stringify(item)); // Additional format
    
    // Notify parent component to store in state
    onDragStart(item);
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  const ACTIVITY_ITEM: DraggableItem = {
    type: 'activity',
    name: t('planning.calendar.dragSidebar.activity') || 'Activité (praticien)',
    icon: '📋',
    color: '#3B82F6',
    calendarTypeOverride: 'practitioner',
  };

  const EMPLOYEE_ACTIVITY_ITEM: DraggableItem = {
    type: 'activity',
    name: t('planning.calendar.dragSidebar.employeeActivity') || 'Activité employé',
    icon: '👥',
    color: '#6366F1',
    calendarTypeOverride: 'employee',
  };

  const APPOINTMENT_ITEM: DraggableItem = {
    type: 'appointment',
    name: t('planning.calendar.dragSidebar.appointment') || 'Rendez-vous',
    icon: '📅',
    color: '#10B981',
  };

  return (
    <div className="w-full bg-white border-r border-gray-200 p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
      <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
        {t('planning.calendar.dragSidebar.title') || 'Glisser pour créer'}
      </h3>

      <div className="space-y-3">
        {/* Activity Item */}
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, ACTIVITY_ITEM)}
          onDragEnd={handleDragEnd}
          className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-gray-300 bg-gradient-to-r from-blue-50 to-blue-100 cursor-move hover:from-blue-100 hover:to-blue-200 hover:border-blue-400 transition-all shadow-sm hover:shadow-md"
          style={{ borderColor: ACTIVITY_ITEM.color + '60' }}
          title={t('planning.calendar.dragSidebar.dragToCreate') || 'Glisser sur le calendrier pour créer une activité'}
        >
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm" style={{ backgroundColor: ACTIVITY_ITEM.color + '20' }}>
            <span className="text-xl">{ACTIVITY_ITEM.icon}</span>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">{ACTIVITY_ITEM.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {t('planning.calendar.dragSidebar.activityHint') || 'Pause, formation, visite...'}
            </div>
          </div>
        </div>

        {/* Employee Activity Item */}
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, EMPLOYEE_ACTIVITY_ITEM)}
          onDragEnd={handleDragEnd}
          className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-gray-300 bg-gradient-to-r from-indigo-50 to-indigo-100 cursor-move hover:from-indigo-100 hover:to-indigo-200 hover:border-indigo-400 transition-all shadow-sm hover:shadow-md"
          style={{ borderColor: EMPLOYEE_ACTIVITY_ITEM.color + '60' }}
          title={t('planning.calendar.dragSidebar.dragToCreateEmployee') || 'Glisser sur le calendrier pour créer une activité employé'}
        >
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm" style={{ backgroundColor: EMPLOYEE_ACTIVITY_ITEM.color + '20' }}>
            <span className="text-xl">{EMPLOYEE_ACTIVITY_ITEM.icon}</span>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">{EMPLOYEE_ACTIVITY_ITEM.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {t('planning.calendar.dragSidebar.employeeActivityHint') || 'Présence, permanence, support...'}
            </div>
          </div>
        </div>

        {/* Appointment Item */}
        {allowAppointments && (
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, APPOINTMENT_ITEM)}
            onDragEnd={handleDragEnd}
            className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-gray-300 bg-gradient-to-r from-green-50 to-green-100 cursor-move hover:from-green-100 hover:to-green-200 hover:border-green-400 transition-all shadow-sm hover:shadow-md"
            style={{ borderColor: APPOINTMENT_ITEM.color + '60' }}
            title={t('planning.calendar.dragSidebar.dragToCreate') || 'Glisser sur le calendrier pour créer un rendez-vous'}
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm" style={{ backgroundColor: APPOINTMENT_ITEM.color + '20' }}>
              <span className="text-xl">{APPOINTMENT_ITEM.icon}</span>
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-800">{APPOINTMENT_ITEM.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {t('planning.calendar.dragSidebar.appointmentHint') || 'Consultation, tatouage, piercing...'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-3 bg-blue-50 rounded-md border border-blue-200">
        <p className="text-xs text-blue-800">
          <strong>{t('planning.calendar.dragSidebar.hint.title') || 'Astuce :'}</strong>{' '}
          {t('planning.calendar.dragSidebar.hint.text') ||
            'Glissez un élément sur le calendrier, puis sélectionnez le type dans la fenêtre de création.'}
        </p>
      </div>
    </div>
  );
}
